import { SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
/**
 * Messaging State
 *
 * Mirrors the Invoicing subdomain 1:1, adapted to the `messaging` connector
 * kind. A provisioned messaging integration is a `SystemIntegration` (the same
 * canonical readmodel shape used by channels/erps/invoicing), so we reuse
 * `SystemIntegrationsReadmodel` rather than inventing a parallel record type.
 *
 * Credential form state is intentionally NOT in CDSE — it lives locally in
 * `AddMessagingModal` (React `useState`), per the established convention.
 */

export type MessagingIntegration = SystemIntegrationsReadmodel;

export interface MessagingState {
  /** Provisioned messaging integrations, keyed by integration id */
  data: {
    integrations: Record<string, MessagingIntegration>;
  };

  /** UI state — add modal + selected connector */
  ui: {
    addModalOpen: boolean;
    selectedConnectorId: string | null;
  };

  /** Loading states */
  loading: {
    listLoading: boolean;
    submitting: boolean;
  };

  /**
   * Transient in-flight submission payload. Populated by `submit/start` so the
   * submit policy (which only receives state in `onStateChange`) can read the
   * connector + credential form values to provision. Cleared on
   * success/error.
   */
  pendingSubmission: {
    connectorId: string;
    credentials: Record<string, string | undefined>;
    /**
     * Display name for a `directProvision` instance, entered in the modal and
     * forwarded by the submit policy as the integration `label` so multiple
     * instances of the same connector are distinguishable in the list.
     */
    label?: string;
  } | null;

  /**
   * Transient in-flight deletion marker. Populated by `delete/start` so the
   * delete policy can identify which integration to remove and distinguish a
   * deletion from a submission (both share `loading.submitting`). Cleared on
   * success/error.
   */
  pendingDeletion: {
    integrationId: string;
  } | null;
}

export const initialState: MessagingState = {
  data: {
    integrations: {}
  },

  ui: {
    addModalOpen: false,
    selectedConnectorId: null
  },

  loading: {
    // Starts true so the list view shows its LoadingOverlay until the
    // list-load policy delivers the first `list/set` snapshot (which clears it).
    listLoading: true,
    submitting: false
  },

  pendingSubmission: null,
  pendingDeletion: null
};

export const selectors = {
  getMessagingIntegration: ( state: MessagingState, integrationId: string ) =>
    state.data.integrations[integrationId],

  selectMessagingIntegrations: ( state: MessagingState ) =>
    Object.values( state.data.integrations ),

  selectAddModalOpen: ( state: MessagingState ) =>
    state.ui.addModalOpen,

  selectedConnectorId: ( state: MessagingState ) =>
    state.ui.selectedConnectorId,

  isListLoading: ( state: MessagingState ) =>
    state.loading.listLoading,

  isSubmitting: ( state: MessagingState ) =>
    state.loading.submitting
};
